import { useMemo, useState, useCallback } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import AddIcon from '@mui/icons-material/Add'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import DeleteIcon from '@mui/icons-material/Delete'
import LockIcon from '@mui/icons-material/Lock'
import { Paper } from '@mui/material'
import Button from '@mui/material/Button'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Tooltip from '@mui/material/Tooltip'
import { object, func, array } from 'prop-types'
import { CvsAutoComplete } from '@components'
import { colors } from '@global/constants'
import { t } from '@lingui/core/macro'

const MarginIcon = styled(ArrowForwardIcon)`
  margin-top: 16px;
  margin-right: 12px;
  margin-left: 12px;
`

const ListItemPadding = styled(ListItem)`
  padding: 12px 0px 0px 0px;
`

const ListNoPadding = styled(List)`
  padding-left: 0px;
`

const margin = css`
  margin-left: 8px;
  margin-top: 12px;
`

const FieldMapperRow = ({
  fromOptions,
  toOptionsFiltered,
  value,
  onSelect,
  onDelete,
  key,
  required,
}) => {
  // Prevent users from mapping data to Claravine ID
  const toOptionsWithoutClaravineID = toOptionsFiltered.filter(el => el.label != 'Claravine ID')

  return (
    <ListItemPadding>
      <Paper
        elevation={3}
        style={{
          padding: 4,
          paddingLeft: 12,
          height: 80,
          position: 'relative',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex' }}>
          <CvsAutoComplete
            key={`from-${key}`}
            style={{ width: 450 }}
            margin="dense"
            options={fromOptions}
            onSelected={val => onSelect([val, value[1] || ''])}
            selected={value[0]}
            disabled={required}
            label={t`Map from`}
            required={required}
          />
          <MarginIcon style={{ height: '40px', color: colors.gray.main, cursor: 'pointer' }} />
          <CvsAutoComplete
            key={`to-${key}`}
            style={{ width: 450 }}
            margin="dense"
            options={toOptionsWithoutClaravineID}
            onSelected={val => onSelect([value[0], val || ''])}
            selected={value[1]}
            label={t`Map to`}
          />
          {/* <IconButton css={margin} onClick={() => onDelete(value[0])}> */}
          {!required ? (
            <div
              css={margin}
              onClick={() => onDelete(value[0])}
              style={{
                display: 'flex',
                alignItems: 'center',
                color: colors.gray.main,
                width: 25,
                cursor: 'pointer',
              }}
            >
              <DeleteIcon />
            </div>
          ) : (
            <div
              css={margin}
              style={{ display: 'flex', alignItems: 'center', width: 25, color: colors.gray.main }}
            >
              <Tooltip title={t`Required mapping`}>
                <LockIcon />
              </Tooltip>
            </div>
          )}
          {/* </IconButton> */}
        </div>
      </Paper>
    </ListItemPadding>
  )
}

FieldMapperRow.propTypes = {
  fromOptions: array.isRequired,
  toOptionsFiltered: array.isRequired,
  value: array.isRequired,
  onSelect: func.isRequired,
  onDelete: func.isRequired,
}

// __________________ BREAK __________________

export const CvsFieldMapper = ({ fromOptions, toOptions, value, onSelect, requiredMappings }) => {
  const toOptionsFiltered = useCallback(
    selected => {
      const filtered = Object.values(value)
      return toOptions.filter(opt => !filtered.includes(opt.value) || opt.value === value[selected])
    },
    [toOptions, value],
  )
  const fromOptionsFiltered = useCallback(
    selected => {
      const filtered = Object.keys(value)
      return fromOptions.filter(opt => {
        return !filtered.includes(opt.value) || value[selected] || value[selected] === ''
      })
    },
    [fromOptions, value],
  )

  const [extraRows, setExtraRows] = useState([])

  const addRow = useCallback(() => {
    setExtraRows(old => (Object.keys(value).length >= old.length ? [...old, old.length] : old))
  }, [setExtraRows, value])

  const mappedExtraRows = useMemo(
    () =>
      !extraRows.length
        ? null
        : extraRows.map((opt, i) => (
            <FieldMapperRow
              key={opt}
              required={false}
              opt=""
              fromOptions={fromOptionsFiltered()}
              toOptionsFiltered={toOptionsFiltered()}
              value=""
              onDelete={() =>
                setExtraRows(old => {
                  const copy = [...old]
                  copy.splice(i, 1)
                  return copy
                })
              }
              onSelect={val => {
                setExtraRows(old => {
                  const copy = [...old]
                  copy.splice(i, 1)
                  return copy
                })
                onSelect({ ...value, [val[0]]: val[1] })
              }}
            />
          )),
    [extraRows, fromOptionsFiltered, onSelect, toOptionsFiltered, value],
  )

  const mappedRows = useMemo(
    () =>
      Object.keys(value).map(key => (
        <FieldMapperRow
          key={key}
          opt={key}
          required={requiredMappings.find(m => m == key) !== undefined}
          fromOptions={fromOptionsFiltered(key)}
          toOptionsFiltered={toOptionsFiltered(key)}
          value={[key, value[key]]}
          onDelete={() => {
            const copy = { ...value }
            delete copy[key]
            onSelect(copy)
          }}
          onSelect={val => {
            const copy = { ...value }
            delete copy[key]
            onSelect(val[0] === key ? { ...value, [key]: val[1] } : { ...copy, [val[0]]: val[1] })
          }}
        />
      )),
    [value, fromOptionsFiltered, toOptionsFiltered, onSelect],
  )
  return (
    <ListNoPadding>
      <Button
        disabled={value.length >= toOptions.length}
        startIcon={<AddIcon />}
        color="primary"
        onClick={addRow}
        variant="contained"
      >
        {t`Add new mapping`}
      </Button>
      {mappedRows}
      {mappedExtraRows}
    </ListNoPadding>
  )
}

CvsFieldMapper.propTypes = {
  fromOptions: array.isRequired,
  toOptions: array.isRequired,
  value: object.isRequired,
  onSelect: func.isRequired,
}
